import { useUserSettingsStore } from "@bundles/settings/components/user-settings/business"
import { getModuleTitleAndPackage } from "@bundles/settings/components/user-settings/components/modules/helpers"
import { Box, Icon, List, ListItemButton, Loader, Tooltip, Typography, styled, useTheme } from "@tm/components"
import { useUser } from "@tm/context-distribution"
import { Demo, Widget } from "@tm/controls"
import { Authority } from "@tm/data"
import { useLocalization } from "@tm/localization"
import { ActivatableModule, ModuleGroupId } from "@tm/models"
import Morpheus from "@tm/morpheus"
import { getModuleCompareRoute, getModuleGroupIconName, getModuleGroupTextId, renderRoute } from "@tm/utils"
import { useMemo } from "react"

type Props = {
    height?: number
    size?: string
    className?: string
    showWidget?: boolean
    moduleRoute: string
}

export default function DemoModuleWidget({ height, size, className, showWidget, moduleRoute }: Props) {
    const { translateText } = useLocalization()
    const { userContext } = useUser()
    const activatableModules = Authority.useGetActivatableModules(userContext)
    const theme = useTheme()

    const loading = useMemo(() => !userContext || activatableModules.isLoading, [userContext, activatableModules])

    const { setOpenUserSettingsDialog, setScrollTo, setFastScroll } = useUserSettingsStore((userSettingsStore) => ({
        setOpenUserSettingsDialog: userSettingsStore.actions.setOpenOrToggleUserSettings,
        setScrollTo: userSettingsStore.actions.setScrollTo,
        setFastScroll: userSettingsStore.actions.setFastScroll,
    }))

    if (!showWidget || !activatableModules.enabled) {
        return null
    }

    function handleClick() {
        setOpenUserSettingsDialog(true)
        setScrollTo("module")
        setFastScroll(true)
    }

    const handleItemClick = (e: React.MouseEvent<HTMLDivElement>, mappedModule: { module: ActivatableModule; moduleGroupId: string }) => {
        e?.stopPropagation?.()
        const moduleKey = getModuleCompareRoute(mappedModule.moduleGroupId as ModuleGroupId)
        if (!moduleKey) {
            return
        }

        const url = renderRoute(moduleRoute, { module: moduleKey })
        Morpheus.showView("1", url)
    }

    const renderStatusIcon = (module: ActivatableModule) => {
        const hasFull = module.moduleOptions?.full?.active
        const hasDemo = module.moduleOptions?.demo?.active
        const demoDays = module.moduleOptions?.demo?.amountOfDays

        const versionActive = hasFull || hasDemo
        const iconName = versionActive ? "unlock" : "lock"

        const demoTooltip =
            hasDemo && demoDays ? `${translateText(14118)}: ${translateText(12573).replace("{0}", demoDays?.toString() || "0")}` : null

        return demoTooltip ? (
            <Tooltip
                title={demoTooltip}
                placement="right"
                componentsProps={{
                    popper: {
                        modifiers: [
                            {
                                name: "offset",
                                options: {
                                    offset: [0, 10],
                                },
                            },
                        ],
                    },
                }}
            >
                <Box>
                    <Icon sx={{ fontSize: "1.8rem", color: theme.colors?.secondary }} name={iconName} />
                    <StyledDemoBadge>
                        <Demo displayMode="edge" />
                    </StyledDemoBadge>
                </Box>
            </Tooltip>
        ) : (
            <Icon sx={{ fontSize: "1.8rem", color: theme.colors?.secondary }} name={iconName} />
        )
    }

    const renderContent = () => {
        if (loading) {
            return (
                <Box display="flex" alignItems="center" width="100%" height="100%">
                    <Loader />
                </Box>
            )
        }

        const mappedModules =
            activatableModules.response?.moduleGroups
                ?.filter((group) => group.display)
                ?.flatMap((group) =>
                    group.modules
                        .filter((m) => m.display)
                        .map((module) => ({
                            module,
                            moduleGroupId: group.id as ModuleGroupId,
                        }))
                ) ?? []

        return (
            <Box display="flex" width="100%" height="100%" gap="0.5em" flexDirection="column" marginBottom="2em">
                {!activatableModules.response && <Typography>{translateText(286)}</Typography>}
                {activatableModules.response && <Typography variant="body3">{`*${translateText(12873)}`}</Typography>}
                <List sx={{ display: "flex", gap: "0.2em", flexDirection: "column" }}>
                    {mappedModules.map(({ module, moduleGroupId }) => {
                        return (
                            <ListItemButton
                                key={`${moduleGroupId}_${module.id}`}
                                sx={{ padding: 0 }}
                                onClick={(e) => handleItemClick(e, { module, moduleGroupId })}
                            >
                                <StyledBox>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "1em" }}>
                                        <Icon size="small" name={getModuleGroupIconName(moduleGroupId)} />
                                        <Typography variant="h4">
                                            {getModuleGroupTextId(moduleGroupId, translateText)} - {getModuleTitleAndPackage(module, translateText)}
                                        </Typography>
                                    </Box>
                                    {renderStatusIcon(module)}
                                </StyledBox>
                            </ListItemButton>
                        )
                    })}
                </List>
            </Box>
        )
    }

    return (
        <Widget
            size={size as any}
            height={height}
            variableHeight
            className={className}
            active
            iconName="modules"
            title={translateText(1832)}
            onClick={handleClick}
        >
            {renderContent()}
        </Widget>
    )
}

const StyledBox = styled(Box)({
    display: "flex",
    alignItems: "center",
    flex: "1",
    padding: "1em",
    border: "1px solid #b4b4b4",
    borderRadius: "3px",
    justifyContent: "space-between",
})

const StyledDemoBadge = styled(Box)({
    position: "absolute",
    top: "0.5em",
    right: "0.4em",
    transform: "scale(1.5)",
})
